// Iter122cv · Pagine di ritorno dal Stripe Checkout (success/cancel)
// Aperto senza autenticazione — usa il session_id come token per polling stato.
import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { CheckCircle2, XCircle, Loader2, RefreshCw, Home } from "lucide-react";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id") || "";
  const [state, setState] = useState({ status: "checking", data: null, error: "" });
  const [tries, setTries] = useState(0);

  const check = async () => {
    if (!sessionId) {
      setState({ status: "error", data: null, error: "session_id mancante nell'URL" });
      return;
    }
    setState((s) => ({ ...s, status: "checking", error: "" }));
    try {
      const { data } = await api.get(`/payment/verify/${sessionId}`);
      if (data.stato === "pagata" || data.paid || data.already_paid) {
        setState({ status: "paid", data, error: "" });
      } else if (data.payment_status === "unpaid") {
        setState({ status: "pending", data, error: "" });
      } else {
        setState({ status: "pending", data, error: "" });
      }
    } catch (e) {
      setState({ status: "error", data: null, error: e?.response?.data?.detail || "Errore verifica pagamento" });
    }
  };

  useEffect(() => { check(); }, [sessionId]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-retry polling: 2s → 4s → 8s (max 3 tentativi automatici)
  useEffect(() => {
    if (state.status !== "pending" || tries >= 3) return;
    const delay = 2000 * Math.pow(2, tries);
    const t = setTimeout(() => { setTries((n) => n + 1); check(); }, delay);
    return () => clearTimeout(t);
  }, [state.status, tries]);  // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FBEEF3] to-white flex items-center justify-center p-4">
      <Card className="w-full max-w-lg p-6 sm:p-8 space-y-4 border-2" data-testid="payment-success-page">
        {state.status === "checking" && (
          <div className="flex flex-col items-center gap-3 py-6">
            <Loader2 className="w-12 h-12 text-brand animate-spin" />
            <h1 className="text-xl font-bold text-slate-800">Verifica pagamento in corso…</h1>
            <p className="text-sm text-slate-600 text-center">Stiamo confermando la transazione con Stripe. Attendi qualche secondo.</p>
          </div>
        )}

        {state.status === "paid" && (
          <div className="flex flex-col items-center gap-3 py-4" data-testid="payment-paid">
            <CheckCircle2 className="w-16 h-16 text-emerald-500" />
            <h1 className="text-2xl font-bold text-emerald-700">Pagamento ricevuto!</h1>
            <p className="text-slate-700 text-center">
              La pro-forma <b>{state.data?.numero}</b> è stata marcata come pagata.
              {state.data?.modalita === "condominio" && " La sub-licenza del condominio è stata rinnovata automaticamente."}
            </p>
            {state.data?.importo && (
              <div className="text-3xl font-bold text-brand mt-2">€ {Number(state.data.importo).toFixed(2)}</div>
            )}
            <div className="mt-4 text-xs text-slate-500 text-center">
              Riceverai a breve la fattura elettronica per email. Puoi chiudere questa pagina in sicurezza.
            </div>
            <div className="flex gap-2 mt-4 flex-wrap justify-center">
              <Link to="/"><Button variant="outline" data-testid="payment-back-home"><Home className="w-4 h-4 mr-1" /> Torna alla home</Button></Link>
            </div>
          </div>
        )}

        {state.status === "pending" && (
          <div className="flex flex-col items-center gap-3 py-4" data-testid="payment-pending">
            <Loader2 className="w-12 h-12 text-amber-500 animate-spin" />
            <h1 className="text-xl font-bold text-amber-700">Pagamento in attesa di conferma</h1>
            <p className="text-sm text-slate-700 text-center">
              Stripe non ha ancora confermato la transazione. Se hai completato il pagamento, il sistema
              dovrebbe aggiornare lo stato entro qualche secondo. Se il problema persiste, ricarica la pagina.
            </p>
            <div className="text-xs text-slate-500">Tentativi automatici: {tries}/3</div>
            <div className="flex gap-2 mt-3 flex-wrap justify-center">
              <Button onClick={() => { setTries(0); check(); }} data-testid="payment-retry"><RefreshCw className="w-4 h-4 mr-1" /> Riprova ora</Button>
              <Link to="/"><Button variant="outline"><Home className="w-4 h-4 mr-1" /> Home</Button></Link>
            </div>
          </div>
        )}

        {state.status === "error" && (
          <div className="flex flex-col items-center gap-3 py-4" data-testid="payment-error">
            <XCircle className="w-12 h-12 text-red-500" />
            <h1 className="text-xl font-bold text-red-700">Errore verifica pagamento</h1>
            <p className="text-sm text-slate-700 text-center">{state.error}</p>
            <div className="flex gap-2 mt-3 flex-wrap justify-center">
              <Button onClick={check}><RefreshCw className="w-4 h-4 mr-1" /> Riprova</Button>
              <Link to="/"><Button variant="outline"><Home className="w-4 h-4 mr-1" /> Home</Button></Link>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-white flex items-center justify-center p-4">
      <Card className="w-full max-w-lg p-6 sm:p-8 space-y-4 border-2" data-testid="payment-cancel-page">
        <div className="flex flex-col items-center gap-3 py-4">
          <XCircle className="w-16 h-16 text-slate-400" />
          <h1 className="text-2xl font-bold text-slate-800">Pagamento annullato</h1>
          <p className="text-slate-700 text-center">
            Hai annullato l&apos;operazione. La pro-forma resta emessa e non pagata:
            puoi riprovare in qualsiasi momento dal link ricevuto per email.
          </p>
          <div className="flex gap-2 mt-3 flex-wrap justify-center">
            <Link to="/"><Button variant="outline" data-testid="payment-cancel-home"><Home className="w-4 h-4 mr-1" /> Home</Button></Link>
          </div>
        </div>
      </Card>
    </div>
  );
}
